import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, CheckCircle2, Package, Undo2, ScanLine, X,
  RotateCcw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import apiClient from '@/services/apiClient';

/**
 * Slice D9.3 — Per-section ComboV2 Return Excess (Salem-untouched).
 *
 * Operator workflow:
 *   1. Pick a plan (PICKING / DISTRIBUTED / ASSEMBLING) for this section.
 *   2. Scan leftover USNs one-by-one — each goes into the session buffer
 *      (single-SKU lock).
 *   3. Wrap the buffer into a fresh BUN code — creates a per-section
 *      SFG inventory row with status PENDING_PUTAWAY (D5 picks it up).
 *   4. Optionally undo the most recent wrapped bundle while it's still
 *      PENDING_PUTAWAY.
 *
 * URL contract:
 *   GET    /api/per-section/{slug}/combo-v2-plans/{plan_id}/return-session
 *   POST   /api/per-section/{slug}/combo-v2-plans/{plan_id}/return-session/scan-usn
 *   POST   /api/per-section/{slug}/combo-v2-plans/{plan_id}/return-session/unscan-usn
 *   POST   /api/per-section/{slug}/combo-v2-plans/{plan_id}/return-session/wrap-bundle
 *   DELETE /api/per-section/{slug}/combo-v2-plans/{plan_id}/return-session/bundle/{bundle_code}
 */

const SLUG_TO_DISPLAY = {
  color_combo: 'Color Combo Section',
  art_silk: 'Art Silk Section',
  kids: 'Kids Section',
};
const ACTIVE_PLAN_STATUSES = ['PICKING', 'DISTRIBUTED', 'ASSEMBLING'];

export default function PerSectionReturnExcessClone() {
  const navigate = useNavigate();
  const { section: slug } = useParams();
  const [params] = useSearchParams();
  const sectionName = SLUG_TO_DISPLAY[slug] || slug;
  const backRoute = `/sections/dispatch/${slug}`;
  const apiBase = `/per-section/${slug}/combo-v2-plans`;

  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(params.get('plan_id') || '');
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState('usn'); // 'usn' | 'bundle'
  const [usnInput, setUsnInput] = useState('');
  const [bundleInput, setBundleInput] = useState('');
  const usnRef = useRef(null);
  const bundleRef = useRef(null);

  const loadPlans = useCallback(async () => {
    try {
      const all = await Promise.all(
        ACTIVE_PLAN_STATUSES.map((s) =>
          apiClient.get(`${apiBase}?status=${s}`).then((r) => r.data.plans || []).catch(() => [])
        ),
      );
      const merged = [].concat(...all);
      setPlans(merged);
    } catch (_e) {
      setPlans([]);
    }
  }, [apiBase]);

  const loadSession = useCallback(async (planId) => {
    if (!planId) { setSession(null); return; }
    setLoading(true);
    try {
      const r = await apiClient.get(`${apiBase}/${planId}/return-session`);
      setSession(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load session');
      setSession(null);
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => { loadPlans(); }, [loadPlans]);
  useEffect(() => { loadSession(selectedPlanId); }, [selectedPlanId, loadSession]);

  // Auto-focus the active input.
  useEffect(() => {
    if (busy || loading) return;
    if (mode === 'usn') usnRef.current?.focus();
    else bundleRef.current?.focus();
  }, [mode, busy, loading, session?.buffer_count]);

  const handleScanUsn = async () => {
    const usn = usnInput.trim();
    if (!usn || !selectedPlanId) return;
    setBusy(true);
    try {
      const r = await apiClient.post(
        `${apiBase}/${selectedPlanId}/return-session/scan-usn`,
        { usn_code: usn },
      );
      toast.success(`Scanned ${r.data.usn.usn_code} (buffer ${r.data.buffer_count})`);
      setUsnInput('');
      await loadSession(selectedPlanId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to scan USN');
    } finally {
      setBusy(false);
    }
  };

  const handleUnscan = async (usnCode) => {
    if (!selectedPlanId) return;
    setBusy(true);
    try {
      await apiClient.post(
        `${apiBase}/${selectedPlanId}/return-session/unscan-usn`,
        { usn_code: usnCode },
      );
      await loadSession(selectedPlanId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove USN');
    } finally {
      setBusy(false);
    }
  };

  const handleWrapBundle = async () => {
    const code = bundleInput.trim().toUpperCase();
    if (!code || !selectedPlanId) return;
    setBusy(true);
    try {
      const r = await apiClient.post(
        `${apiBase}/${selectedPlanId}/return-session/wrap-bundle`,
        { bundle_code: code },
      );
      toast.success(`Bundle ${code} saved as PENDING PUT AWAY`);
      setBundleInput('');
      setMode('usn');
      if (r.data.plan_completed) {
        toast.success('Plan auto-completed — all leftover USNs returned.');
      }
      await loadSession(selectedPlanId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to wrap bundle');
    } finally {
      setBusy(false);
    }
  };

  const handleUndoBundle = async (bundleCode) => {
    if (!window.confirm(`Undo bundle ${bundleCode}? USNs go back to the scan buffer.`)) return;
    setBusy(true);
    try {
      await apiClient.delete(
        `${apiBase}/${selectedPlanId}/return-session/bundle/${encodeURIComponent(bundleCode)}`,
      );
      toast.success('Bundle undone — USNs restored to buffer');
      await loadSession(selectedPlanId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to undo bundle');
    } finally {
      setBusy(false);
    }
  };

  const isCompleted =
    session?.plan_status === 'COMPLETED' || session?.session_status === 'completed';
  const bufferCount = session?.buffer_count || 0;
  const canWrap = bufferCount > 0 && !isCompleted;

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid={`per-section-return-excess-${slug}`}>
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate(backRoute)}
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Return Excess</h1>
              <p className="text-sm text-[#E8DCC8]">{sectionName} · ComboV2 leftover wrap</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { loadPlans(); loadSession(selectedPlanId); }}
            disabled={busy || loading}
            className="border-white/20 text-white hover:bg-white/10"
            data-testid="refresh-btn"
          >
            <RotateCcw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 md:px-6 py-6 space-y-4">
        {/* Plan picker */}
        <Card className="p-4 space-y-3">
          <div className="text-sm font-medium">Pick a plan to wrap leftovers for</div>
          <Select value={selectedPlanId} onValueChange={setSelectedPlanId}>
            <SelectTrigger data-testid="plan-select">
              <SelectValue placeholder="Pick a plan" />
            </SelectTrigger>
            <SelectContent>
              {plans.length === 0 ? (
                <SelectItem value="__none__" disabled>No active plans</SelectItem>
              ) : (
                plans.map((p) => (
                  <SelectItem key={p.plan_id} value={p.plan_id}>
                    {p.plan_id} · {p.status} · {p.plan_name || ''}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </Card>

        {!selectedPlanId && (
          <div className="text-center text-stone-500 py-12 text-sm" data-testid="empty-state">
            Pick a plan to begin scanning leftovers.
          </div>
        )}

        {selectedPlanId && loading && (
          <div className="flex justify-center py-12" data-testid="loading-state">
            <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
          </div>
        )}

        {selectedPlanId && session && (
          <>
            {isCompleted && (
              <Alert
                className="bg-emerald-50 border-emerald-300"
                data-testid="plan-completed-alert"
              >
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                <AlertTitle className="text-emerald-800">Plan Completed</AlertTitle>
                <AlertDescription className="text-emerald-700">
                  All leftover USNs returned. Bundles are queued for SFG Put-Away — the SFG
                  operator will scan a bin and tag each bundle's location.
                </AlertDescription>
              </Alert>
            )}

            {/* Summary tiles */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card className="p-3" data-testid="tile-total-leftover">
                <div className="text-xs text-stone-500">Total Leftover</div>
                <div className="text-2xl font-bold">{session.total_leftover_pcs ?? 0}</div>
              </Card>
              <Card className="p-3" data-testid="tile-remaining">
                <div className="text-xs text-stone-500">Remaining to Scan</div>
                <div className="text-2xl font-bold text-amber-700">
                  {session.remaining_pcs ?? 0}
                </div>
              </Card>
              <Card className="p-3" data-testid="tile-buffer">
                <div className="text-xs text-stone-500">In Buffer (unwrapped)</div>
                <div className="text-2xl font-bold text-blue-700">{bufferCount}</div>
              </Card>
              <Card className="p-3" data-testid="tile-wrapped">
                <div className="text-xs text-stone-500">Wrapped into Bundles</div>
                <div className="text-2xl font-bold text-emerald-700">
                  {session.scanned_into_bundles_pcs ?? 0}
                </div>
              </Card>
            </div>

            {/* Pending by SKU */}
            {(session.pending_by_sku || []).length > 0 && !isCompleted && (
              <Card className="p-3" data-testid="pending-by-sku-card">
                <div className="text-xs text-stone-500 mb-2">Pending by SKU</div>
                <div className="flex flex-col gap-2">
                  {(session.pending_by_sku || []).map((g) => (
                    <Badge
                      key={g.sku_code}
                      variant="outline"
                      className="px-3 py-2 flex items-start justify-between gap-3 w-full"
                      data-testid={`pending-sku-${g.sku_code}`}
                    >
                      <div className="flex flex-col text-left">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs">{g.sku_code}</span>
                          <span className="text-stone-400">·</span>
                          <span className="font-medium">{g.design_name}</span>
                        </div>
                        <div className="text-xs text-stone-500 mt-0.5">
                          {[g.shade, g.size].filter(Boolean).join(' · ') || '—'}
                        </div>
                      </div>
                      <span className="text-amber-700 font-semibold whitespace-nowrap">
                        {g.pcs} pcs
                      </span>
                    </Badge>
                  ))}
                </div>
              </Card>
            )}

            {/* Scan / Wrap panel */}
            {!isCompleted && (
              <Card className="p-4 space-y-3">
                <div className="flex gap-2">
                  <Button
                    variant={mode === 'usn' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setMode('usn')}
                    disabled={busy}
                    data-testid="mode-usn-btn"
                  >
                    <ScanLine className="h-4 w-4 mr-1" />
                    1. Scan USN
                  </Button>
                  <Button
                    variant={mode === 'bundle' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setMode('bundle')}
                    disabled={busy || bufferCount === 0}
                    data-testid="mode-bundle-btn"
                  >
                    <Package className="h-4 w-4 mr-1" />
                    2. Wrap Bundle ({bufferCount})
                  </Button>
                </div>

                {mode === 'usn' && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Scan leftover USN</label>
                    <div className="flex gap-2">
                      <Input
                        ref={usnRef}
                        value={usnInput}
                        onChange={(e) => setUsnInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleScanUsn()}
                        placeholder="Scan or type USN code"
                        disabled={busy}
                        className="font-mono"
                        data-testid="usn-input"
                      />
                      <Button
                        onClick={handleScanUsn}
                        disabled={busy || !usnInput.trim()}
                        data-testid="usn-scan-btn"
                      >
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Scan'}
                      </Button>
                    </div>
                    {session.buffer_sku && (
                      <p className="text-xs text-amber-700">
                        Buffer locked to SKU{' '}
                        <span className="font-mono font-semibold">{session.buffer_sku}</span> —
                        wrap the current bundle to switch SKUs.
                      </p>
                    )}
                  </div>
                )}

                {mode === 'bundle' && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">
                      Scan the fresh Bundle Code
                    </label>
                    <div className="flex gap-2">
                      <Input
                        ref={bundleRef}
                        value={bundleInput}
                        onChange={(e) => setBundleInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && canWrap && handleWrapBundle()}
                        placeholder="BUN######/YY-YY"
                        disabled={busy || !canWrap}
                        className="font-mono uppercase"
                        data-testid="bundle-input"
                      />
                      <Button
                        onClick={handleWrapBundle}
                        disabled={busy || !canWrap || !bundleInput.trim()}
                        data-testid="wrap-bundle-btn"
                      >
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Wrap'}
                      </Button>
                    </div>
                    <p className="text-xs text-stone-600">
                      Stick the printed bundle barcode on the leftover stack and scan it here.
                      The new bundle is saved as <strong>PENDING PUT AWAY</strong>.
                    </p>
                  </div>
                )}
              </Card>
            )}

            {/* Buffer */}
            {bufferCount > 0 && (
              <Card className="p-3" data-testid="buffer-card">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-medium">
                    Buffer — {bufferCount} USN(s)
                    {session.buffer_sku ? ` · SKU ${session.buffer_sku}` : ''}
                  </div>
                  <Button
                    size="sm"
                    variant="default"
                    onClick={() => setMode('bundle')}
                    disabled={busy || isCompleted}
                    data-testid="proceed-to-wrap-btn"
                  >
                    <Package className="h-4 w-4 mr-1" />
                    Proceed to Wrap
                  </Button>
                </div>
                <div className="max-h-48 overflow-y-auto divide-y divide-stone-100 rounded border">
                  {(session.buffer_usns || []).map((u) => (
                    <div
                      key={u.usn_code}
                      className="px-3 py-1.5 flex items-center justify-between text-sm"
                      data-testid={`buffer-usn-${u.usn_code}`}
                    >
                      <span className="font-mono">{u.usn_code}</span>
                      <span className="text-xs text-stone-500">
                        {u.design_name || '-'} · {u.shade || '-'} · {u.size || '-'}
                      </span>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleUnscan(u.usn_code)}
                        disabled={busy}
                        className="h-6 w-6"
                        data-testid={`unscan-${u.usn_code}`}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Wrapped bundles */}
            {(session.bundles || []).length > 0 && (
              <Card className="p-3" data-testid="wrapped-card">
                <div className="text-sm font-medium mb-2">
                  Wrapped Bundles ({(session.bundles || []).length}) — Pending Put Away
                </div>
                <div className="divide-y divide-stone-100 rounded border">
                  {(session.bundles || []).map((b) => (
                    <div
                      key={b.bundle_code}
                      className="px-3 py-2 flex items-center justify-between"
                      data-testid={`wrapped-bundle-${b.bundle_code}`}
                    >
                      <div>
                        <div className="font-mono text-sm font-semibold">{b.bundle_code}</div>
                        <div className="text-xs text-stone-500">
                          SKU {b.sku_code} · {b.design_name} · {(b.usn_codes || []).length} pcs
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge
                          variant="outline"
                          className="bg-amber-50 text-amber-800 border-amber-300"
                        >
                          PENDING PUT AWAY
                        </Badge>
                        {!isCompleted && (
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleUndoBundle(b.bundle_code)}
                            disabled={busy}
                            className="h-7 w-7"
                            data-testid={`undo-${b.bundle_code}`}
                            title="Undo (move USNs back to buffer)"
                          >
                            <Undo2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </>
        )}
      </main>
    </div>
  );
}
